import { useEffect } from "react";
import { Slip } from "../components/Slip";
import { EDUCATION, EXPERIENCE, SUMMARY_LEAD, SUMMARY_REST } from "../lib/resume";

/** /resume and /resume/slate_auto — a one-page Letter resume in the site's own
 *  design language (Slipstream display type, IBM Plex Mono body, etched labels,
 *  pixel-shadow panels). The sheet is pinned to the LIGHT (warm paper) tokens so it
 *  prints and exports cleanly; the surround follows the site theme on screen.
 *
 *  The copy lives in lib/resume.tsx (shared with the site's work section). The only
 *  difference between variants is the Rivian PS line. */

export type ResumeVariant = "normal" | "slate_auto";

const VARIANTS: { id: ResumeVariant; href: string; label: string; title: string }[] = [
  { id: "normal", href: "/resume", label: "normal", title: "AJ Gerstenhaber Resume" },
  {
    id: "slate_auto",
    href: "/resume/slate_auto",
    label: "slate_auto",
    title: "AJ Gerstenhaber Resume - Slate Auto",
  },
];

export function Resume({ variant }: { variant: ResumeVariant }) {
  const meta = VARIANTS.find((v) => v.id === variant)!;
  useEffect(() => {
    const prev = document.title;
    // The browser's "Save as PDF" names the file from the title.
    document.title = meta.title;
    return () => {
      document.title = prev;
    };
  }, [meta.title]);

  return (
    <div className="resume-desk min-h-dvh px-4 py-6 md:py-10">
      {/* Screen-only toolbar */}
      <div className="resume-toolbar mx-auto mb-5 flex max-w-[816px] flex-wrap items-center gap-2">
        <a href="/" className="etch-label mr-auto !text-[12px]">
          ← grstnhbr.com
        </a>
        {VARIANTS.map((v) => (
          <a
            key={v.id}
            href={v.href}
            className={`btn !px-3 !py-1 !text-[12px] ${v.id === variant ? "btn-primary glow" : ""}`}
            aria-current={v.id === variant ? "page" : undefined}
          >
            {v.label}
          </a>
        ))}
        <button className="btn btn-primary !px-3 !py-1 !text-[12px]" onClick={() => window.print()}>
          print / pdf
        </button>
      </div>

      <article data-theme="light" className="resume-sheet mx-auto">
        {/* Header: name, contact, summary — photo right */}
        <header className="flex items-start gap-6">
          <div className="min-w-0 flex-1">
            <h1 className="text-[32px] leading-none text-amber">
              <Slip k="name" />
            </h1>
            <div className="resume-contact mt-2.5">
              <span>
                <b>website</b> <a href="https://atxfilmlab.com">atxfilmlab.com</a> /{" "}
                <a href="https://grstnhbr.com">grstnhbr.com</a>
              </span>
              <span>
                <b>email</b> <a href="mailto:ajg@duck.com">ajg@duck.com</a>
              </span>
              <span>
                <b>phone</b> <a href="tel:+15127973101">(512) 797-3101</a>
              </span>
            </div>
            <p className="resume-summary mt-4">
              <strong>{SUMMARY_LEAD}</strong> {SUMMARY_REST}
            </p>
          </div>
          <img
            src="/resume/aj.jpg"
            alt="AJ Gerstenhaber holding a calico cat"
            className="resume-photo"
          />
        </header>

        <h2 className="resume-h2 text-amber">
          <Slip k="experience" />
        </h2>

        <div className="flex flex-col gap-3">
          {EXPERIENCE.map((e) => (
            <section
              key={e.key}
              className="resume-panel"
              style={{ ["--accent-bar" as string]: e.accent }}
            >
              {e.roles.map((r, i) => (
                <div key={r.when} className={i > 0 ? "mt-2.5" : ""}>
                  <div className="flex items-end gap-2.5">
                    <span className="resume-when">{r.when}</span>
                    <img src={e.logo} alt={e.logoAlt} className="resume-logo" style={{ height: e.logoH }} />
                    <span className="resume-role ml-auto">{r.title}</span>
                  </div>
                  <ul className="resume-bullets">
                    {r.bullets.map((b, j) => (
                      <li key={j}>{b}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </section>
          ))}
        </div>

        <h2 className="resume-h2 text-amber">
          <Slip k="education" />
        </h2>

        <div className="flex items-baseline gap-4">
          <span className="resume-role !text-text">{EDUCATION.school}</span>
          <span className="resume-role ml-auto">{EDUCATION.place}</span>
        </div>
        <p className="resume-degree">
          <em>{EDUCATION.degree}</em>
        </p>

        {variant === "slate_auto" && (
          <p className="resume-ps">
            <img src="/resume/rivian-l.png" alt="" aria-hidden="true" />
            <span>PS: also a Rivian owner and car nerd.</span>
            <img src="/resume/rivian-r.png" alt="" aria-hidden="true" />
          </p>
        )}
      </article>
    </div>
  );
}
